/**
 * PrintingMaterialSelectedWebcad Component
 *
 * Fetches print-start event and parses materialLayers3DCache from rawContent.
 * Displays material (TPU + density) and color for each layer.
 *
 * Mimics hardware-digester behavior with React Admin 5.x and Material-UI v5.
 */

import { useState, useEffect } from 'react';
import { useDataProvider, useRecordContext } from 'react-admin';
import capitalize from 'lodash/capitalize';
import { InputLabel, LinearProgress } from '@mui/material';
import { Printing, EventType, Event } from '../../types';

interface MaterialLayer {
  colorKey?: string;
  density?: number;
}

interface MaterialLayers3DCache {
  layers: number;
  [key: `layer${number}`]: MaterialLayer;
}

interface PrintStartEventContent {
  fields?: {
    metadata?: {
      materialLayers3DCache?: MaterialLayers3DCache;
    };
  };
}

interface MaterialData {
  material: string;
  color: string;
}

export const PrintingMaterialSelectedWebcad = () => {
  const record = useRecordContext<Printing>();
  const printingId = record?.id;
  const provider = useDataProvider();
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<MaterialData[]>([]);

  useEffect(() => {
    async function fetchEventPrintStart(): Promise<Event | undefined> {
      const { data: eventTypes } = await provider.getList<EventType>('eventTypes', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'name', order: 'ASC' },
        filter: { name: 'print-start' },
      });
      const { data: events } = await provider.getList<Event>('events', {
        pagination: { page: 1, perPage: 1 },
        sort: { field: 'eventTime', order: 'ASC' },
        filter: { eventTypeId: eventTypes?.[0]?.id, printingId },
      });
      return events?.[0];
    }

    async function getData() {
      try {
        setError(false);
        setLoading(true);
        setData([]);

        const event = await fetchEventPrintStart();
        if (event?.rawContent) {
          const content: PrintStartEventContent = JSON.parse(event.rawContent);
          if (content.fields?.metadata?.materialLayers3DCache) {
            const numLayers = +content.fields.metadata.materialLayers3DCache.layers;
            const materials: MaterialData[] = [];

            if (typeof numLayers === 'number' && numLayers > 0) {
              for (let i = 0; i < numLayers; i++) {
                const layer = content.fields.metadata.materialLayers3DCache[
                  `layer${i}` as keyof MaterialLayers3DCache
                ] as MaterialLayer | undefined;

                if (!layer?.colorKey || !layer?.density) continue;

                materials.unshift({
                  material: 'TPU ' + layer.density,
                  color: capitalize(layer.colorKey),
                });
              }
              setData(materials);
            }
          }
        }
      } catch {
        setError(true);
      }
      setLoading(false);
    }

    if (printingId) {
      getData();
    } else {
      setLoading(false);
    }
  }, [printingId, provider]);

  return (
    <>
      <InputLabel shrink={true} sx={{ margin: '8px 0 4px 0' }}>
        <span>Material Selected WebCAD</span>
      </InputLabel>
      {error ? <span>Sorry, an error occurred.</span> : null}
      {!error && loading ? (
        <div style={{ width: 60 }}>
          <LinearProgress />
        </div>
      ) : !data?.length ? (
        <span>{'N/A'}</span>
      ) : (
        data.map(({ material, color }, i) => (
          <p key={i} style={{ margin: 0 }}>
            {material} - {color}
          </p>
        ))
      )}
    </>
  );
};

PrintingMaterialSelectedWebcad.defaultProps = {
  label: 'Material Selected WebCAD',
};
